import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLojistaAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ArrowLeft, ArrowRight, MessageSquare, Store } from "lucide-react";

export default function LojistaLogin() {
  const { lojista, requestToken, verifyToken, error } = useLojistaAuth();
  const [step, setStep] = useState("identifier"); // identifier | token
  const [identificador, setIdentificador] = useState("");
  const [token, setToken] = useState("");
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (lojista) navigate("/lojista/dashboard");
  }, [lojista, navigate]);

  const solicitar = async (e) => {
    e.preventDefault();
    setLoading(true);
    const resp = await requestToken(identificador);
    setLoading(false);
    if (resp) {
      setMeta(resp);
      setStep("token");
    }
  };

  const verificar = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await verifyToken(identificador, token);
    setLoading(false);
    if (ok) navigate("/lojista/dashboard");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="max-w-md mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="loj-back-home">
            <ArrowLeft size={14} /> Início
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-foreground text-background flex items-center justify-center font-heading font-black text-sm rounded-md">
              VM
            </div>
            <span className="font-heading font-black text-sm">VM SHOPPING</span>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-md w-full mx-auto px-5 py-10">
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto bg-foreground text-background flex items-center justify-center rounded-2xl mb-6">
            <Store size={28} />
          </div>
          <div className="label-tag mb-2">Portal do Lojista</div>
          <h1 className="font-heading font-black tracking-tighter text-3xl">
            {step === "identifier" ? "Acesso via WhatsApp" : "Digite o código"}
          </h1>
        </div>

        {step === "identifier" ? (
          <form onSubmit={solicitar} className="space-y-5">
            <div className="space-y-2">
              <Label className="label-tag">CNPJ ou Telefone cadastrado</Label>
              <Input
                data-testid="loj-identificador"
                value={identificador}
                onChange={(e) => setIdentificador(e.target.value)}
                required
                placeholder="00.000.000/0001-00 ou +5511..."
                className="h-14 rounded-xl text-base font-mono"
              />
            </div>

            {error && (
              <div data-testid="loj-login-error" className="text-sm border border-destructive/40 bg-destructive/5 text-destructive px-4 py-3 rounded-xl">
                {error}
              </div>
            )}

            <Button
              data-testid="loj-solicitar-token"
              type="submit"
              disabled={loading}
              className="w-full h-14 rounded-xl bg-foreground text-background hover:bg-foreground/90 text-base"
            >
              <MessageSquare size={16} className="mr-2" />
              {loading ? "Enviando..." : "Enviar código via WhatsApp"}
              <ArrowRight size={16} className="ml-2" />
            </Button>

            <div className="text-xs text-muted-foreground text-center leading-relaxed pt-4">
              Enviaremos um código de 6 dígitos no WhatsApp cadastrado para você acessar seu portal.
            </div>
          </form>
        ) : (
          <form onSubmit={verificar} className="space-y-5">
            <div className="text-center text-sm text-muted-foreground">
              Código enviado para <b className="text-foreground font-mono">{meta?.telefone_mascarado}</b>
            </div>

            <div className="flex justify-center py-4">
              <InputOTP maxLength={6} value={token} onChange={setToken} data-testid="loj-otp-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="w-12 h-14 text-lg rounded-md" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            {error && (
              <div className="text-sm border border-destructive/40 bg-destructive/5 text-destructive px-4 py-3 rounded-xl">
                {error}
              </div>
            )}

            <Button
              data-testid="loj-verificar-token"
              type="submit"
              disabled={loading || token.length < 6}
              className="w-full h-14 rounded-xl bg-foreground text-background hover:bg-foreground/90 text-base"
            >
              {loading ? "Verificando..." : "Acessar minha conta"}
            </Button>

            <button
              type="button"
              onClick={() => { setStep("identifier"); setToken(""); }}
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground py-2"
              data-testid="loj-voltar-step"
            >
              Trocar identificação
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
